import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useRequireAuth } from '../contexts/AuthContext';
import { getProfile, createCheckoutSession, getBillingPortalUrl } from '../services/api';

/**
 * User profile page showing account info.
 * Requires authentication — redirects to /login if not signed in.
 */
function ProfileView() {
  const { isLoading: authLoading } = useRequireAuth('/profile');
  const { user, isPro, refreshUser } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [billingError, setBillingError] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    loadProfile();
  }, [authLoading]);

  // Returning from a completed Stripe Checkout: the ID token still carries the pre-upgrade
  // custom:subscription_tier claim, so isPro must be re-derived from a forced session refresh
  // before it's read anywhere (here or elsewhere in the app), not from the stale cached token.
  useEffect(() => {
    if (searchParams.get('upgraded') === '1') {
      refreshUser(true).then(() => {
        navigate('/profile', { replace: true });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadProfile() {
    setLoading(true);
    setError(null);
    try {
      const profileData = await getProfile();
      setProfile(profileData);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpgradeClick() {
    setBillingError(null);
    setBillingLoading(true);
    try {
      const { url } = await createCheckoutSession();
      window.location.href = url;
    } catch {
      setBillingError('Failed to start checkout. Please try again.');
      setBillingLoading(false);
    }
  }

  async function handleManageBilling() {
    setBillingError(null);
    setBillingLoading(true);
    try {
      const { url } = await getBillingPortalUrl();
      window.location.href = url;
    } catch {
      setBillingError('Failed to open billing portal. Please try again.');
      setBillingLoading(false);
    }
  }

  if (authLoading || loading) {
    return (
      <div className='loading'>
        <div className='spinner' />
        <p>Loading profile...</p>
      </div>
    );
  }

  if (error) {
    return <div className='error-banner'>{error}</div>;
  }

  // Postgres UserProfile.tier is written inconsistently-cased across backend call sites
  // (StripeWebhookService writes "PRO"/"FREE", ProfileService writes "free") -- every backend
  // comparison against it already tolerates this via equalsIgnoreCase, so match that here too
  // instead of the strict === this used to be, which silently showed "Free" for real Pro users.
  const isProfileTierPro = profile?.tier?.toLowerCase() === 'pro';

  return (
    <div>
      <div className='card'>
        <h2 style={{ marginTop: 0 }}>My Profile</h2>
        <p><strong>Email:</strong> {profile?.email || user?.email}</p>
        <p><strong>Display Name:</strong> {profile?.displayName || '—'}</p>
        <p>
          <strong>Plan:</strong>{' '}
          <span className={`pos-badge ${isProfileTierPro ? 'pos-QB' : 'pos-K'}`}>
            {isProfileTierPro ? 'Pro' : 'Free'}
          </span>
        </p>
        {billingError && <div className='error-banner'>{billingError}</div>}
        {isPro ? (
          <button className='btn btn-secondary' onClick={handleManageBilling} disabled={billingLoading}>
            {billingLoading ? 'Loading…' : 'Manage Billing'}
          </button>
        ) : (
          <button className='btn btn-primary' onClick={handleUpgradeClick} disabled={billingLoading}>
            {billingLoading ? 'Loading…' : 'Upgrade to Pro'}
          </button>
        )}
      </div>
    </div>
  );
}

export default ProfileView;
